import type { Point, Stroke } from "./types";
import { CAPTURE_HEIGHT, CAPTURE_WIDTH, INK, PAPER } from "./types";

/**
 * Line width as a function of pen force.
 *
 * Anchored so pressure 1 lands on 2px — the width signed.tsx draws with — which
 * means a mouse capture (always normalised to 1) looks exactly like the live
 * site, while a pen's lighter passes taper below it.
 */
const MIN_WIDTH = 0.8;
const MAX_WIDTH = 2.0;

export function widthFor(pressure: number): number {
  return MIN_WIDTH + (MAX_WIDTH - MIN_WIDTH) * Math.min(1, Math.max(0, pressure));
}

/**
 * The drawing space is always 400x240 regardless of how big the pad is on screen —
 * that coordinate space is part of the file format. CSS decides the displayed size
 * (a phone narrower, an iPad far wider), and `fit` scales the context to match, so
 * a signature drawn on either still exports in the space signed.tsx expects.
 */
export function prepare(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas is unavailable in this browser");

  fit(canvas, ctx);
  return ctx;
}

/**
 * Sizes the backing store to what the pad actually occupies on screen, then scales
 * the context so drawing still happens in 400x240 space.
 *
 * The store used to be a flat 400x240 device pixels. That was right when the pad
 * was 400 CSS px wide and wrong the moment it wasn't: an iPad landscape pad is
 * around 1100 CSS px at dpr 2, so the ink was being drawn into a bitmap under a
 * third of the width it was stretched across. What you were signing on looked
 * soft, which reads as an unsteady hand and invites you to go back over a line.
 *
 * Returns whether the store changed, because assigning width or height wipes the
 * canvas and resets its state — callers have to redraw, and we skip the work when
 * nothing moved.
 */
export function fit(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): boolean {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();

  // Before first layout the pad has no size; fall back to the capture space so a
  // context is always usable rather than one device pixel across.
  const cssWidth = rect.width || CAPTURE_WIDTH;
  const cssHeight = rect.height || CAPTURE_HEIGHT;

  const width = Math.max(1, Math.round(cssWidth * dpr));
  const height = Math.max(1, Math.round(cssHeight * dpr));
  if (canvas.width === width && canvas.height === height) return false;

  canvas.width = width;
  canvas.height = height;

  // Re-applied rather than set once: sizing the store resets the whole context.
  ctx.setTransform(width / CAPTURE_WIDTH, 0, 0, height / CAPTURE_HEIGHT, 0, 0);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = INK;
  return true;
}

export function clear(ctx: CanvasRenderingContext2D): void {
  ctx.clearRect(0, 0, CAPTURE_WIDTH, CAPTURE_HEIGHT);
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, CAPTURE_WIDTH, CAPTURE_HEIGHT);
}

/**
 * One segment per call rather than a single long path: width varies per segment,
 * and a stroked path can only carry one lineWidth.
 */
export function segment(ctx: CanvasRenderingContext2D, from: Point, to: Point): void {
  ctx.lineWidth = widthFor((from.pressure + to.pressure) / 2);
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
}

/** `paper: false` leaves the background transparent — used for the PNG export. */
export function drawAll(
  ctx: CanvasRenderingContext2D,
  strokes: Stroke[],
  { paper = true }: { paper?: boolean } = {},
): void {
  if (paper) clear(ctx);
  for (const stroke of strokes) {
    for (let i = 1; i < stroke.points.length; i++) {
      segment(ctx, stroke.points[i - 1], stroke.points[i]);
    }
  }
}
